import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppButton from '../../../../shared/components/app-button/AppButton';
import AppChip from '../../../../shared/components/app-chip/AppChip';
import AppRadioInput from '../../../../shared/components/app-radio-input/AppRadioInput';
import CruiseLayout, {
  CruiseBackLink,
} from '../../components/common/CruiseLayout';
import CruiseTripSummary from '../../components/common/CruiseTripSummary';
import { cruiseCabinsApi, cruisePriceCheckApi } from '../../api/cruiseApi';
import { CRUISE_ROUTES } from '../../constants/cruiseConstants';
import { flowUpTo, useCruiseFlow } from '../../utils/cruiseFlow';
import {
  bedLabel,
  cabinLabel,
  cabinShortLabel,
  diningLabel,
  diningRoomLabel,
  diningStatus,
  fareTypeLabel,
  formatAmount,
  guestCount,
  guestsSummary,
  isGuaranteed,
  listFrom,
  metaLabel,
  sizeLabel,
  titleCase,
  wholePartyTotal,
} from '../../utils/cruiseUtils';

const NOT_ASSIGNED = /^to be assigned$/i;

/** Whether a stateroom takes a party of this size. */
const cabinFits = (cabin, party) =>
  (!cabin.maximumGuests || party <= cabin.maximumGuests) &&
  (!cabin.minimumGuests || party >= cabin.minimumGuests);

const cabinTags = (cabin) =>
  [
    ...new Set(
      (cabin.locations || [])
        .filter((l) => l && !NOT_ASSIGNED.test(l))
        .map(titleCase),
    ),
  ].concat(isGuaranteed(cabin) ? ['Assigned by cruise line'] : []);

/** First option the line says is available, else the first one. */
const defaultDining = (options) =>
  (options.find((d) => diningStatus(d).tone === 'success') || options[0])
    ?.diningCode || '';

function CruiseCabinPage() {
  const navigate = useNavigate();
  const flow = useCruiseFlow();
  const { cruise, fare, category, search } = flow;

  const [cabins, setCabins] = useState([]);
  const [diningOptions, setDiningOptions] = useState([]);
  const [cabinNo, setCabinNo] = useState(flow.cabin?.cabinNumber || '');
  const [bedCode, setBedCode] = useState(flow.bed?.bedCode || '');
  const [diningCode, setDiningCode] = useState(flow.dining?.diningCode || '');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!cruise || !fare || !category) {
      navigate(CRUISE_ROUTES.LANDING, { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    cruiseCabinsApi(
      { fareSource: cruise.fareSource, bookingKey: category.bookingKey },
      controller.signal,
    ).then((read) => {
      if (controller.signal.aborted) return;
      const list = read.ok ? listFrom(read.data, 'cabins') : [];
      const dining = read.ok ? listFrom(read.data?.diningOptions) : [];
      setCabins(list);
      setDiningOptions(dining);
      const party = guestCount(search || {});
      const fitting = list.filter((c) => cabinFits(c, party));
      setCabinNo((no) =>
        no && fitting.some((c) => c.cabinNumber === no)
          ? no
          : fitting[0]?.cabinNumber || '',
      );
      setDiningCode((code) =>
        code && dining.some((d) => d.diningCode === code)
          ? code
          : defaultDining(dining),
      );
      if (!read.ok) {
        setAlert({
          variant: 'error',
          title: 'Cabins',
          description: read.network
            ? 'Could not reach the server. Please try again.'
            : read.message || 'Could not load cabins.',
        });
      }
      setLoading(false);
    });
    return () => controller.abort();
  }, []);

  const cabin = cabins.find((c) => c.cabinNumber === cabinNo);
  const beds = cabin?.beds || [];

  // Keep the bed valid for whichever cabin is selected.
  useEffect(() => {
    if (!beds.length) {
      setBedCode('');
    } else if (!beds.some((b) => b.bedCode === bedCode)) {
      setBedCode(beds[0].bedCode);
    }
  }, [cabinNo, cabins]);

  if (!cruise || !fare || !category) return null;

  const bed = beds.find((b) => b.bedCode === bedCode) || null;
  const dining = diningOptions.find((d) => d.diningCode === diningCode) || null;

  const handleContinue = async () => {
    if (!cabin) return;
    if (diningOptions.length && !dining) {
      setAlert({
        variant: 'error',
        title: 'Dining',
        description: 'Please choose a dining preference.',
      });
      return;
    }
    setLoading(true);
    const read = await cruisePriceCheckApi({
      fareSource: cruise.fareSource,
      bookingKey: cabin.bookingKey,
    });
    setLoading(false);
    if (!read.ok) {
      setAlert({
        variant: 'error',
        title: 'Price check',
        description: read.network
          ? 'Could not reach the server. Please try again.'
          : read.message ||
            'This cabin could not be priced. Please pick another cabin.',
      });
      return;
    }
    navigate(CRUISE_ROUTES.GUESTS, {
      state: flowUpTo(flow, 'category', {
        cabin,
        bed,
        dining,
        priceCheck: read.data,
        priceCheckedAt: Date.now(),
      }),
    });
  };

  const size = sizeLabel(
    category.smallestCabinSquareFeet,
    category.largestCabinSquareFeet,
  );
  const changeCategory = () =>
    navigate(CRUISE_ROUTES.DETAILS, { state: flowUpTo(flow, 'category') });
  const total = formatAmount(wholePartyTotal(category));

  return (
    <CruiseLayout
      step="cabin"
      loading={loading}
      alert={alert}
      onAlertHide={() => setAlert(null)}
    >
      <CruiseBackLink label="Change category" onClick={changeCategory} />
      <div className="cr-split cr-split--with-bar">
        <section className="cr-split__main">
          <div className="cr-panel cr-panel--pad">
            <div>
              <b className="cr-h3">Choose your cabin</b>
              <div className="cr-muted">
                {[
                  `Category ${category.categoryCode}`,
                  titleCase(category.categoryName),
                  size,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            {!loading && cabins.length === 0 && (
              <div className="cr-empty-inline">
                No cabins are available in this category.{' '}
                <button
                  type="button"
                  className="cr-link"
                  onClick={changeCategory}
                >
                  Choose another category
                </button>
              </div>
            )}
            {cabins.map((c) => {
              const on = c.cabinNumber === cabinNo;
              const fits = cabinFits(c, guestCount(search || {}));
              const pick = () => fits && setCabinNo(c.cabinNumber);
              const guests =
                c.minimumGuests && c.maximumGuests
                  ? `${c.minimumGuests}–${c.maximumGuests} guests`
                  : '';
              const bedNames = (c.beds || []).map(bedLabel).join(' or ');
              return (
                <div
                  key={c.cabinNumber}
                  className={`cr-option ${on ? 'is-selected' : ''} ${
                    fits ? '' : 'is-disabled'
                  }`}
                  role="button"
                  tabIndex={fits ? 0 : -1}
                  aria-disabled={!fits}
                  onClick={pick}
                  onKeyDown={(e) => e.key === 'Enter' && pick()}
                >
                  <AppRadioInput
                    id={`cr-cabin-${c.cabinNumber}`}
                    name="cruiseCabin"
                    value={c.cabinNumber}
                    checked={on}
                    disabled={!fits}
                    onChange={pick}
                  />
                  <div className="cr-option__main">
                    <b>{cabinLabel(c)}</b>
                    <span className="cr-muted-sm">
                      {isGuaranteed(c) && 'Deck assigned later'}
                      {!isGuaranteed(c) &&
                        c.deckName &&
                        `${titleCase(c.deckName)} deck`}
                    </span>
                  </div>
                  <div className="cr-tags">
                    {cabinTags(c).map((t) => (
                      <span className="cr-tag" key={t}>
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="cr-option__aside">
                    {guests && (
                      <span className={fits ? '' : 'cr-text-danger'}>
                        {guests}
                        {fits ? '' : ' · not for your party'}
                      </span>
                    )}
                    {bedNames && <span>{bedNames}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {beds.length > 0 && (
            <div className="cr-panel cr-panel--pad">
              <b className="cr-h4">Bed preference</b>
              <div className="cr-chips">
                {beds.map((b) => (
                  <AppChip
                    key={b.bedCode}
                    name="bed"
                    value={b.bedCode}
                    chipText={bedLabel(b)}
                    selected={b.bedCode === bedCode}
                    className="cr-chip cr-chip--lg"
                    onClick={() => setBedCode(b.bedCode)}
                  />
                ))}
              </div>
            </div>
          )}

          {diningOptions.length > 0 && (
            <div className="cr-panel cr-panel--pad">
              <div>
                <b className="cr-h4">Dining preference</b>
                <div className="cr-muted">
                  Applies to all guests in this cabin
                </div>
              </div>
              <div className="cr-options-grid">
                {diningOptions.map((d) => {
                  const on = d.diningCode === diningCode;
                  const status = diningStatus(d);
                  return (
                    <div
                      key={d.diningCode}
                      className={`cr-option cr-option--compact ${
                        on ? 'is-selected' : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDiningCode(d.diningCode)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && setDiningCode(d.diningCode)
                      }
                    >
                      <AppRadioInput
                        id={`cr-dining-${d.diningCode}`}
                        name="cruiseDining"
                        value={d.diningCode}
                        checked={on}
                        onChange={() => setDiningCode(d.diningCode)}
                      />
                      <div className="cr-option__main">
                        <b>{diningLabel(d)}</b>
                        <span className="cr-muted-sm">
                          {diningRoomLabel(d)}
                        </span>
                        {status && (
                          <span
                            className={`cr-badge cr-badge--${status.tone} cr-badge--start`}
                          >
                            {status.label}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <aside className="cr-split__aside">
          <CruiseTripSummary
            cruise={cruise}
            rows={[
              { label: 'Fare', value: fareTypeLabel(fare.fareType) },
              {
                label: 'Category',
                value: [category.categoryCode, metaLabel(category.meta)]
                  .filter(Boolean)
                  .join(' · '),
              },
              { label: 'Cabin', value: cabin ? cabinShortLabel(cabin) : '' },
              { label: 'Bed', value: bed ? bedLabel(bed) : '' },
              { label: 'Dining', value: dining ? diningLabel(dining) : '' },
              { label: 'Guests', value: guestsSummary(search || {}) },
            ]}
          >
            <div className="cr-total-row">
              <span>Total (whole party)</span>
              <b className="cr-amount cr-amount--md">{total}</b>
            </div>
            <AppButton
              btnText="Continue to Guest Details"
              className="cr-hide-mobile"
              onClick={handleContinue}
              disabled={!cabin}
              style={{ height: '46px', fontWeight: 600, fontSize: '15px' }}
            />
            <div className="cr-muted-xs cr-center">
              Final price is confirmed with the cruise line when you continue.
            </div>
          </CruiseTripSummary>
        </aside>
      </div>

      <div className="cr-mobile-bar">
        <div>
          <div className="cr-muted-xs">Total (whole party)</div>
          <b className="cr-amount cr-amount--sm">{total}</b>
        </div>
        <AppButton
          btnText="Continue"
          onClick={handleContinue}
          disabled={!cabin}
          style={{ height: '44px', padding: '0 22px', fontWeight: 600 }}
        />
      </div>
    </CruiseLayout>
  );
}

export default CruiseCabinPage;
